import { useMeetingWarning } from 'components/meetings/hooks';
import { MeetingWarningIndicator } from 'components/meetings/MeetingWarningIndicator';
import { isInProgress, isStartingSoon, type WarningKey } from 'components/meetings/utils';
import { GenerateMeetingPrep, useGenerateMeetingPrep } from 'features/meeting_prep';
import { useMeetingsContext } from 'features/meetings/contexts/MeetingsContext';
import { useNotetakerState, NotetakerDisplayMode } from 'features/meetings/hooks';
import type { NotetakerState } from 'features/meetings/hooks/use_notetaker_state';
import type { Meeting } from 'features/meetings/types';
import { useCurrentTime } from 'shared/contexts';
import { JoinButton } from './JoinButton';
import { MeetingDot } from './MeetingDot';
import { MeetingListLiveStatus } from './MeetingListLiveStatus';
import { MeetingSummary } from './MeetingSummary';
import { MeetingTime } from './MeetingTime';
import { NotetakerControl } from './notetaker_control';
import { type MeetingLiveStatus, useMeetingLiveStatus } from './useMeetingLiveStatus';
import { isMultiHost } from './utils';
const timeAreaCss = "tb0kjyr";
const summaryAreaCss = "s7fv8ri";
const dotCss = "d83hecl";
const summaryContentCss = "smgcywc";
const statusAreaCss = "s180ea22";
const actionsAreaCss = "a1uyxs7h";
const notetakerAreaCss = "n1coy8qi";
const meetingContentCss = "m1dmdff1";
const meetingContentWithStatusCss = "m1sbja08";
const meetingContentMidWidthCss = "m1ed3hmg";
const meetingContentMidWidthWithStatusCss = "m16kmdlk";
interface MeetingCardContentProps {
  event: Meeting;
  otherEventsOnDay?: Meeting[];
  isNext?: boolean;
  anyInProgress?: boolean;
  isMostRecentInProgress?: boolean;
  packageName?: string | null;
  onboardingTourMeetingRefCallback?: (el: HTMLElement | null) => void;
  onboardingTourNotetakerRefCallback?: (el: HTMLElement | null) => void;
  isFirstUpcomingForTour?: boolean;
  hovered: boolean;
  isSelected: boolean;
  isCompact: boolean;
  isMidWidth: boolean;
  setChildButtonHovered: (hovered: boolean) => void;
}

/**
 * Pre-computed display model for {@link MeetingCardContentView}. Produced by
 * {@link MeetingCardContent}; can be passed explicitly in Storybook/tests.
 */
export type MeetingCardContentViewProps = Omit<MeetingCardContentProps, 'otherEventsOnDay' | 'isNext' | 'anyInProgress'> & {
  liveStatus: MeetingLiveStatus;
  notetakerState: NotetakerState;
  showGenerateMeetingPrep: boolean;
  contact: unknown;
  hasWarningFeatures: boolean;
  warningKey: WarningKey | null;
  /** From MeetingsContext — forwarded to JoinButton */
  hasRealtimeFeatures: boolean;
  showJoinButton: boolean;
};

/**
 * Pure layout for the inner content of a meeting card. Receives all hook-derived
 * state via props so Storybook and tests can render permutations without
 * mocking multiple providers.
 */
export const MeetingCardContentView = ({
  event,
  isMostRecentInProgress = false,
  packageName = null,
  onboardingTourMeetingRefCallback,
  onboardingTourNotetakerRefCallback,
  isFirstUpcomingForTour = false,
  hovered,
  isSelected,
  isCompact,
  isMidWidth,
  setChildButtonHovered,
  liveStatus,
  notetakerState,
  showGenerateMeetingPrep,
  contact,
  hasWarningFeatures,
  warningKey,
  hasRealtimeFeatures,
  showJoinButton
}: MeetingCardContentViewProps) => {
  const showNotetaker = notetakerState.displayMode !== NotetakerDisplayMode.HIDDEN;
  const showEventActions = showJoinButton || showGenerateMeetingPrep;
  const {
    cancelled,
    past: isPast,
    source
  } = event;
  const color = event.eventType?.color ?? '';
  return <div className={[meetingContentCss, meetingContentMidWidthCss, liveStatus.hasContent && meetingContentWithStatusCss, liveStatus.hasContent && meetingContentMidWidthWithStatusCss].filter(Boolean).join(' ')}>
      {liveStatus.hasContent && <div className={statusAreaCss}>
          <MeetingListLiveStatus liveStatus={liveStatus} shouldUseContrast={isSelected || hovered} />
        </div>}
      {!isCompact && !isMidWidth && <MeetingTime className={timeAreaCss} ref={isFirstUpcomingForTour ? onboardingTourMeetingRefCallback : undefined} event={event} />}
      <div className={summaryAreaCss}>
        <div className={dotCss}>
          <MeetingDot source={source} color={color} isPast={isPast} isCancelled={cancelled} />
        </div>
        <div className={summaryContentCss}>
          <MeetingSummary event={event} packageName={packageName}>
            {(isCompact || isMidWidth) && <MeetingTime ref={isFirstUpcomingForTour ? onboardingTourMeetingRefCallback : undefined} event={event} />}
            {hasWarningFeatures && warningKey && <MeetingWarningIndicator warningKey={warningKey} />}
          </MeetingSummary>
        </div>
      </div>
      {showEventActions && <div className={actionsAreaCss}>
          {showGenerateMeetingPrep && <GenerateMeetingPrep contact={contact} meeting={event} />}
          {showJoinButton && <JoinButton event={event} setHovered={setChildButtonHovered} isMostRecentInProgress={isMostRecentInProgress} hasRealtimeFeatures={hasRealtimeFeatures} />}
        </div>}
      {showNotetaker && <div className={notetakerAreaCss}>
          <NotetakerControl ref={isFirstUpcomingForTour ? onboardingTourNotetakerRefCallback : undefined} event={event} notetakerState={notetakerState} />
        </div>}
    </div>;
};

/**
 * Renders the inner content layout of a meeting card.
 *
 * Composes the meeting dot, time, summary, live status badge, notetaker
 * control, join button, and meeting prep action into a responsive grid.
 * Layout switches between desktop (side-by-side) and compact (stacked)
 * modes based on the container width observed by the parent `MeetingCard`.
 *
 * @param event - The meeting whose data is rendered
 * @param otherEventsOnDay - Other meetings on the same day, passed to live status logic
 * @param isNext - Whether this is the next upcoming meeting
 * @param anyInProgress - Whether any meeting in the list is currently in progress
 * @param isMostRecentInProgress - Whether this is the most recent in-progress meeting
 * @param packageName - Package booking name to display, if applicable
 * @param onboardingTourMeetingRefCallback - Ref callback for the MI tour meeting anchor
 * @param onboardingTourNotetakerRefCallback - Ref callback for the MI tour notetaker anchor
 * @param isFirstUpcomingForTour - Whether this card is the tour's target upcoming meeting
 * @param hovered - Whether the parent card link is hovered (controls join button visibility)
 * @param isSelected - Whether this meeting is the currently selected one
 * @param isCompact - Whether the card is in compact (narrow) layout mode
 * @param isMidWidth - Whether the card is in mid-width layout mode (time inlines, right side stays horizontal)
 * @param setChildButtonHovered - Callback to notify the parent card when a child button is hovered
 */
export const MeetingCardContent = ({
  event,
  otherEventsOnDay,
  isNext,
  anyInProgress,
  isMostRecentInProgress = false,
  packageName = null,
  onboardingTourMeetingRefCallback,
  onboardingTourNotetakerRefCallback,
  isFirstUpcomingForTour = false,
  hovered,
  isSelected,
  isCompact,
  isMidWidth,
  setChildButtonHovered
}: MeetingCardContentProps) => {
  const {
    hasRealtimeFeatures
  } = useMeetingsContext() ?? {};
  const currentTime = useCurrentTime();
  const {
    hasRealtimeFeatures: hasWarningFeatures,
    warningKey
  } = useMeetingWarning(event);
  const {
    showGenerateMeetingPrep,
    contact
  } = useGenerateMeetingPrep(event);
  const liveStatus = useMeetingLiveStatus(event, {
    otherEventsOnDay,
    isPackageBooking: !!packageName
  });
  const notetakerState = useNotetakerState(event);
  const {
    cancelled
  } = event;
  const inProgress = isInProgress(event, currentTime);
  const showJoinButton = !isMultiHost(event) && !cancelled && (inProgress || (hasRealtimeFeatures ? isStartingSoon(event, currentTime) : isNext && !anyInProgress));
  return <MeetingCardContentView event={event} isMostRecentInProgress={isMostRecentInProgress} packageName={packageName} onboardingTourMeetingRefCallback={onboardingTourMeetingRefCallback} onboardingTourNotetakerRefCallback={onboardingTourNotetakerRefCallback} isFirstUpcomingForTour={isFirstUpcomingForTour} hovered={hovered} isSelected={isSelected} isCompact={isCompact} isMidWidth={isMidWidth} setChildButtonHovered={setChildButtonHovered} liveStatus={liveStatus} notetakerState={notetakerState} showGenerateMeetingPrep={showGenerateMeetingPrep} contact={contact} hasWarningFeatures={hasWarningFeatures} warningKey={warningKey} hasRealtimeFeatures={!!hasRealtimeFeatures} showJoinButton={showJoinButton} />;
};

require("./MeetingCardContent.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./MeetingCardContent.tsx");